/**
 * =============================================================================
 * MOCK DATA - STATIC DATA SOURCE FOR WORKFLOW BUILDER
 * =============================================================================
 * 
 * PURPOSE:
 * This file contains all mock/static data used by the Automations feature.
 * Currently serves as the ONLY data source (no backend integration).
 * 
 * IMPORTANT FOR DRAG-AND-DROP:
 * The `workflowSteps` array is the KEY DATA STRUCTURE that needs to become
 * stateful for drag-and-drop to work. Currently it's a static export.
 * 
 * TO ENABLE DYNAMIC WORKFLOW EDITING:
 * 1. Move workflowSteps to React state in AutomationsContainer
 * 2. Create handlers: addStep(stepData, index), removeStep(id), reorderSteps(fromIndex, toIndex)
 * 3. Use useReducer if state logic becomes complex
 * 
 * DATA EXPORTS:
 * - workflows: List of saved workflow templates (for library view)
 * - pausedLeads: Leads that paused in workflows (for attention tab)
 * - monitorLeads: Leads currently active in workflows (for monitor panel)
 * - workflowSteps: THE WORKFLOW SEQUENCE (most important for DnD)
 * - guardrailItems: Safety rules configuration options
 * =============================================================================
 */
import { Play, MessageSquare, Clock, Mail } from 'lucide-react';

/**
 * WORKFLOWS - Saved workflow templates
 * Displayed in AutomationLibrary component as cards
 * 
 * Schema:
 * - id: Unique identifier
 * - name: Display name
 * - icon: Icon type string (not used currently)
 * - trigger: What starts the workflow
 * - active: Number of leads currently in workflow
 * - openRate: Email open rate percentage
 * - replies: Reply rate percentage
 * - lastEdited: Last modification time
 * - enabled: Whether workflow is active
 */
export const workflows = [
  {
    id: 1,
    name: 'Open House Follow-up',
    icon: 'home',
    trigger: 'Form Submit',
    active: 14,
    openRate: '62%',
    replies: '8%',
    lastEdited: '2d ago',
    enabled: true
  },
  {
    id: 2,
    name: 'Cold Lead Revival',
    icon: 'thermometer',
    trigger: 'Manual Add',
    active: 0,
    openRate: '--',
    replies: '--',
    lastEdited: '5d ago',
    enabled: false
  }
];

export const pausedLeads = [
  { name: 'Pam Beesly', email: 'pam@dm.com', workflow: 'Open House Follow-up', step: 'SMS #1', reason: 'Lead Replied', time: '2h ago' },
  { name: 'Jim Halpert', email: 'jim@dm.com', workflow: 'Open House Follow-up', step: 'Wait 1 Day', reason: 'Lead Replied', time: '4h ago' },
  { name: 'Ryan Howard', email: 'ryan@dm.com', workflow: 'Cold Lead Revival', step: 'Step 1', reason: 'Archived', time: '1d ago' },
];

export const monitorLeads = [
  { name: 'Jim Halpert', property: '123 Main St', step: 'Wait 1 Day', status: 'Running', note: '-' },
  { name: 'Dwight Schrute', property: 'Schrute Farms', extra: '+ 2 others', step: 'Send Email', status: 'Running', note: 'Processing multiple objects...' },
  { name: 'Pam Beesly', property: 'Art Studio Lofts', step: 'Send SMS', status: 'Stopped', note: 'Lead Replied' },
  { name: 'Michael Scott', property: 'Condo #4', step: 'Wait 1 Day', status: 'Rejected', note: 'Manually Stopped by Agent' },
];

/**
 * =============================================================================
 * WORKFLOW STEPS - THE CORE DATA STRUCTURE FOR THE WORKFLOW CANVAS
 * =============================================================================
 * 
 * This is the PRIMARY DATA STRUCTURE that defines a workflow's sequence.
 * Each object represents one step in the workflow.
 * 
 * SCHEMA:
 * {
 *   id: string,        // Unique identifier for this step instance
 *   type: string,      // Step type: 'trigger' | 'email' | 'sms' | 'wait'
 *   title: string,     // Display title shown in the node
 *   subtitle: string,  // Description/preview text
 *   icon: Component,   // Lucide React icon component
 * }
 * 
 * ORDERING:
 * - Array index determines display order (index 0 = top/first)
 * - First step MUST always be type 'trigger'
 * - Subsequent steps can be any action type
 * 
 * FOR DRAG-AND-DROP IMPLEMENTATION:
 * 
 * 1. CONVERT TO STATE:
 *    // In AutomationsContainer.jsx
 *    const [workflowSteps, setWorkflowSteps] = useState(initialSteps);
 * 
 * 2. ADD STEP HANDLER:
 *    const addStep = (stepType, insertIndex) => {
 *      const newStep = {
 *        id: `${stepType}-${Date.now()}`,  // Generate unique ID
 *        type: stepType,
 *        title: getDefaultTitle(stepType),
 *        subtitle: getDefaultSubtitle(stepType),
 *        icon: getIconForType(stepType),
 *      };
 *      const newSteps = [...workflowSteps];
 *      newSteps.splice(insertIndex, 0, newStep);
 *      setWorkflowSteps(newSteps);
 *    };
 * 
 * 3. REORDER HANDLER:
 *    const reorderSteps = (fromIndex, toIndex) => {
 *      // Don't allow moving trigger (index 0)
 *      if (fromIndex === 0 || toIndex === 0) return;
 *      const newSteps = [...workflowSteps];
 *      const [removed] = newSteps.splice(fromIndex, 1);
 *      newSteps.splice(toIndex, 0, removed);
 *      setWorkflowSteps(newSteps);
 *    };
 * 
 * 4. REMOVE STEP HANDLER:
 *    const removeStep = (stepId) => {
 *      // Don't allow removing trigger
 *      const step = workflowSteps.find(s => s.id === stepId);
 *      if (step?.type === 'trigger') return;
 *      setWorkflowSteps(workflowSteps.filter(s => s.id !== stepId));
 *    };
 * 
 * STEP TYPE DEFAULTS:
 * - email: { title: 'Send Email', subtitle: 'Subject: ...', icon: Mail }
 * - sms: { title: 'Send SMS', subtitle: 'Hi {{First Name}}...', icon: MessageSquare }
 * - wait: { title: 'Wait 1 Day', subtitle: 'Pauses workflow', icon: Clock }
 * =============================================================================
 */
export const workflowSteps = [
  { id: 'trigger', type: 'trigger', title: 'Start Trigger', subtitle: 'Form Submit on 123 Main', icon: Play },
  { id: 'sms', type: 'sms', title: 'Send SMS', subtitle: 'Hi {{First Name}}, thanks for coming...', icon: MessageSquare },
  { id: 'wait', type: 'wait', title: 'Wait 1 Day', subtitle: 'Pauses workflow for 1 day', icon: Clock },
  { id: 'email', type: 'email', title: 'Send Email', subtitle: 'Subject: Quick question about...', icon: Mail },
];

export const guardrailItems = [
  { title: 'Stop on Reply', desc: 'Immediately pause if lead replies via SMS, Email, or Phone.' },
  { title: 'Stop on Status Change', desc: null, checkboxes: ['Stop if "Closed"', 'Stop if "Archived"'] },
  { title: 'Stop on Weekends', desc: 'Pause all execution on Saturday & Sunday. Resumes automatically on Monday.' },
];

export const guardrailItemsMobile = [
  { title: 'Stop on Reply', desc: 'Immediately pause if lead replies via SMS, Email, or Phone.' },
  { title: 'Stop on Status Change', desc: null, checkboxes: ['Stop if "Closed"', 'Stop if "Archived"'] },
  { title: 'Stop on Weekends', desc: 'Pause all execution on Saturday & Sunday. Resumes automatically on Monday.' },
];
